import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, RefreshCw } from "lucide-react";
import { hoyMX } from "@/lib/utils";
import api, { formatApiError } from "@/lib/api";

const hoy = () => hoyMX();
const hace = (d) => new Date(Date.now() - d * 864e5).toISOString().slice(0, 10);

const Kpi = ({ l, v, testid, sub }) => <div className="rounded-xl bg-brand-surface px-3 py-2 min-w-[110px]" data-testid={testid}><div className="eyebrow">{l}</div><div className="font-display font-extrabold text-xl text-brand-ink">{v}</div>{sub && <div className="text-[10px] text-brand-muted">{sub}</div>}</div>;

export default function CuponesPanel() {
  const [desde, setDesde] = useState(hace(30));
  const [hasta, setHasta] = useState(hoy());
  const [data, setData] = useState(null);
  const [exp, setExp] = useState(null);
  const [maxPct, setMaxPct] = useState(25);

  const cargar = useCallback(async () => {
    try { const r = await api.get("/cupones/stats", { params: { desde, hasta } }); setData(r.data); } catch (e) { toast.error(formatApiError(e)); }
  }, [desde, hasta]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { api.get("/config").then((r) => setMaxPct(r.data.promo_max_pct || 25)).catch(() => {}); }, []);

  async function guardarMax() {
    try { const { data: cfg } = await api.get("/config"); await api.put("/config", { ...cfg, promo_max_pct: maxPct }); toast.success(`Descuento máximo de la promo del día: ${maxPct}% (aplica desde la próxima generación)`); } catch (e) { toast.error(formatApiError(e)); }
  }

  const t = data?.total;
  return (
    <section className="surface-card p-5 space-y-4" data-testid="cupones-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h2 className="font-display font-bold text-brand-ink flex items-center gap-2"><Ticket className="w-4 h-4 text-brand-amber" /> Cupones de regreso (promo del día IA)</h2><p className="text-xs text-brand-muted">Cada ticket lleva un cupón de 5 días con la esencia de menor venta. Aquí mides si la promo trae clientes de regreso.</p></div>
        <div className="flex items-center gap-2 text-xs">
          <input type="date" data-testid="cupones-desde" value={desde} onChange={(e) => setDesde(e.target.value)} className="field h-9 w-36" />
          <span className="text-brand-muted">a</span>
          <input type="date" data-testid="cupones-hasta" value={hasta} onChange={(e) => setHasta(e.target.value)} className="field h-9 w-36" />
          <button data-testid="cupones-refrescar" onClick={cargar} className="btn-soft h-9 px-3"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {t && (
        <div className="flex flex-wrap gap-2">
          <Kpi l="Emitidos" v={t.emitidos} testid="cupones-emitidos" />
          <Kpi l="Canjeados" v={t.canjeados} testid="cupones-canjeados" sub={`${t.tasa_canje}% de canje`} />
          <Kpi l="Vigentes" v={t.vigentes} testid="cupones-vigentes" />
          <Kpi l="Vencidos" v={t.vencidos} testid="cupones-vencidos" />
          <div className="rounded-xl border border-dashed border-brand-line px-3 py-2 flex items-center gap-2 text-xs" data-testid="promo-max-pct">
            <span className="eyebrow">Descuento máximo</span>
            <input type="number" min={10} max={50} step={5} value={maxPct} onChange={(e) => setMaxPct(Number(e.target.value))} data-testid="promo-max-pct-input" className="field h-8 w-16 text-center font-bold" />%
            <button data-testid="promo-max-pct-guardar" onClick={guardarMax} className="btn-ink h-8 px-3 text-xs">Guardar</button>
            <span className="text-brand-muted hidden md:inline">último lugar = máximo · penúltimo −5 · antepenúltimo −10</span>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="cupones-tabla">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-brand-muted border-b border-brand-line"><th className="py-2 pr-3">Sucursal</th><th className="py-2 pr-3 text-right">Emitidos</th><th className="py-2 pr-3 text-right">Canjeados</th><th className="py-2 pr-3 text-right">Tasa</th><th className="py-2 pr-3 text-right">Vigentes</th><th className="py-2 text-right">Vencidos</th></tr></thead>
          <tbody>
            {(data?.sucursales || []).map((s) => (
              <>
                <tr key={s.sucursal_id} data-testid={`cupones-suc-${s.sucursal_id}`} onClick={() => setExp(exp === s.sucursal_id ? null : s.sucursal_id)} className="border-b border-brand-line/60 hover:bg-brand-surface cursor-pointer">
                  <td className="py-2 pr-3 font-semibold text-brand-ink">{s.sucursal_nombre}</td><td className="py-2 pr-3 text-right font-mono">{s.emitidos}</td><td className="py-2 pr-3 text-right font-mono">{s.canjeados}</td>
                  <td className="py-2 pr-3 text-right"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${s.tasa_canje >= 20 ? "bg-emerald-100 text-emerald-800" : s.tasa_canje > 0 ? "bg-brand-amber-soft text-brand-ink" : "bg-brand-surface text-brand-muted"}`}>{s.tasa_canje}%</span></td>
                  <td className="py-2 pr-3 text-right font-mono">{s.vigentes}</td><td className="py-2 text-right font-mono">{s.vencidos}</td>
                </tr>
                {exp === s.sucursal_id && <tr key={`${s.sucursal_id}-e`}><td colSpan={6} className="bg-brand-surface/60 px-3 py-2 text-xs"><div className="flex flex-wrap gap-2">{s.esencias.map((e) => <span key={e.nombre} className="px-2 py-1 rounded-lg bg-white border border-brand-line">{e.nombre} <b>{e.pct}%</b> · {e.canjeados}/{e.emitidos} canjeados</span>)}</div></td></tr>}
              </>
            ))}
            {data && data.sucursales.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-brand-muted text-xs">Sin cupones emitidos en el rango.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
